import { Color } from '@persona-id/design-tokens';
import _, { parseInt } from 'lodash';
import styled, { css } from 'styled-components';

import BarChartWithLegend from 'dashboard/components/BarChartWithLegend';
import { RenderLabelProps } from 'dashboard/components/BarChartWithLegend/BarChartWithLegend';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { AutoResizableText } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/AutoResizableText';

export type BarValue = { _id: string } & Record<string, number>;

export interface FunnelConversionBarChartProps {
  data: BarValue[];
  seriesFormatter: (string: string) => string;
  stepIds: string[];
  isAbsolute: boolean;
  isCompact: boolean;
  chartHeight?: number;
  xAxisHeight?: number;
}

const convertToPercentages = (data: BarValue[], isAbsolute: boolean): BarValue[] => {
  return data.map((value, idx) => {
    const stepId = value._id;
    // this can be the previous step once we add the toggle for relative percentages.
    const referenceIndex = isAbsolute ? 0 : Math.max(0, idx - 1);
    const percentage = value[stepId] / data[referenceIndex][data[referenceIndex]._id];
    return {
      ...value,
      [stepId]: isNaN(percentage) ? 0 : percentage * 100,
    };
  });
};

export const DEFAULT_PERCENTAGE_TICKS = [0, 25, 50, 75, 100];
const DESIRED_FONT_SIZE = 16;

const isDataEmpty = (percentageData: BarValue[]) => {
  return (
    percentageData.find((data) =>
      Object.values(data).find((value) => typeof value === 'number' && value > 0),
    ) == null
  );
};

const FunnelConversionBarChartContainer = styled.div<{ isCompact: boolean }>`
  position: relative;
  ${({ isCompact }) => {
    if (isCompact) {
      return css`
        top: -8px;
      `;
    }
  }}
`;

const formatText = (percentage: number | string, value: number | string | null) => {
  const formattedPercentage = `${formatNumber(
    typeof percentage === 'number' ? percentage : parseInt(percentage),
  )}%`;
  if (value == null) {
    return formattedPercentage;
  }
  return `${formattedPercentage} ∙ ${formatNumber(
    typeof value === 'number' ? value : parseInt(value),
  )}`;
};

const FunnelConversionBarChart = ({
  data,
  seriesFormatter,
  stepIds,
  isAbsolute,
  isCompact,
  chartHeight,
  xAxisHeight,
}: FunnelConversionBarChartProps) => {
  if (data.length === 0) {
    return null;
  }
  const percentageData = convertToPercentages(data, isAbsolute);
  const largestPercentage = _.max(percentageData.flatMap((barData) => Object.values(barData))) || 0;
  const largestValue = _.max(data.flatMap((barData) => Object.values(barData))) || 0;
  const longestFormattedLength = formatText(largestPercentage, largestValue).length;
  // if our data is empty (i.e. all zeroes) we hardcode our ticks to be 0 to 100.
  const dataEmpty = isDataEmpty(percentageData);
  return (
    <FunnelConversionBarChartContainer isCompact={isCompact}>
      <BarChartWithLegend
        data={percentageData}
        barKeys={stepIds}
        barColors={[Color.CorePurple600]}
        stackId='id'
        createTooltipHeader={() => ''}
        barKeyFormatter={seriesFormatter}
        hideLegend={true}
        renderTooltip={() => null}
        xDataKey={(data: BarValue) => seriesFormatter(data._id)}
        // the total size of the chart is height + xAxisHeight
        chartHeight={chartHeight}
        xAxisHeight={xAxisHeight ?? 90}
        xAxisTick={<AutoResizableText />}
        xFormatter={(name) => name}
        yAxisUnit='%'
        yAxisTicks={dataEmpty ? DEFAULT_PERCENTAGE_TICKS : undefined}
        renderBarLabel={(props: RenderLabelProps, idx: number) => {
          const { x, y, fill, value, index, width } = props;
          if (idx !== index) {
            return null;
          }
          // this is (likely an over) estimation of how much space the longest text will take up.
          // we then use it to calculate the font size for the labels.
          const requiredWidth = (DESIRED_FONT_SIZE * longestFormattedLength) / 2;
          const effectiveFontSize = Math.min(1, width / requiredWidth) * DESIRED_FONT_SIZE;
          return (
            <text
              x={x + width / 2}
              y={y}
              dy={-10}
              fontSize={effectiveFontSize}
              fontFamily='sans-serif'
              fill={fill}
              textAnchor='middle'
            >
              {formatText(value as number, isCompact ? null : (data[idx][data[idx]._id] as number))}
            </text>
          );
        }}
        barIsAnimationActive={!dataEmpty && !isCompact}
      />
    </FunnelConversionBarChartContainer>
  );
};

export default FunnelConversionBarChart;
